import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, Building2, User, ExternalLink, Loader2, Check, Eye, EyeOff, DollarSign, Filter } from "lucide-react";
import { toast } from "sonner";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const STATUS_OPTIONS = [
  { v: "", label: "All Statuses" },
  { v: "pending", label: "Pending" },
  { v: "intro_sent", label: "Intro Sent" },
  { v: "contacted", label: "Contacted" },
  { v: "signed_up", label: "Signed Up" },
  { v: "declined", label: "Declined" },
];

export default function AdminRecommendations() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const res = await axios.get(`${API_V1}/admin/recommendations`, {
        headers: getAuthHeader(),
        params,
      });
      setItems(res.data.recommendations || []);
      setCounts(res.data.counts_by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterType]);

  const patch = async (id, payload) => {
    setSavingId(id);
    try {
      const res = await axios.patch(`${API_V1}/admin/recommendations/${id}`, payload, { headers: getAuthHeader() });
      const updated = res.data.recommendation;
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-recommendations">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white font-['Outfit']">Recommendations</h1>
          <p className="text-[#94A3B8] text-sm mt-1 max-w-2xl">
            Pipeline of businesses and agents recommended by your listing owners. Approve, link to live listings, and toggle Trusted Network visibility.
          </p>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="rounded-lg bg-white/5 border border-white/10 p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{k.replace("_", " ")}</p>
            <p className="text-white font-bold text-xl">{v}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-[#94A3B8]" />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
          style={{ colorScheme: "dark" }}
          data-testid="admin-rec-filter-status"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v} className="bg-[#0F172A]">{o.label}</option>)}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
          style={{ colorScheme: "dark" }}
          data-testid="admin-rec-filter-type"
        >
          <option value="" className="bg-[#0F172A]">All Types</option>
          <option value="business" className="bg-[#0F172A]">Business</option>
          <option value="agent" className="bg-[#0F172A]">Agent</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="h-32 bg-white/5 rounded-xl animate-pulse" />
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
          <Sparkles className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
          <p className="text-[#94A3B8] text-sm">No recommendations match your filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <AdminRecCard key={r.id} rec={r} saving={savingId === r.id} onPatch={(p) => patch(r.id, p)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminRecCard({ rec, saving, onPatch }) {
  const [linkedSlug, setLinkedSlug] = useState(rec.linked_slug || "");
  const [notes, setNotes] = useState(rec.admin_notes || "");
  const [showNotes, setShowNotes] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-[#0F172A] border border-white/10 p-4 md:p-5"
      data-testid={`admin-rec-${rec.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
            {rec.recommendee_type === "business" ? <Building2 className="w-5 h-5 text-[#00E5FF]" /> : <User className="w-5 h-5 text-[#EC4899]" />}
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{rec.recommendee_name}</p>
            <p className="text-[#94A3B8] text-xs mt-0.5">
              {rec.recommendee_email || "—"} · {rec.recommendee_phone || "—"}
            </p>
            <p className="text-[#64748B] text-[11px] mt-0.5 flex flex-wrap items-center gap-2">
              <span className="capitalize">{rec.recommendee_type}</span>
              {rec.recommendee_category && <span>· {rec.recommendee_category}</span>}
              {(rec.recommendee_city || rec.recommendee_state) && <span>· {[rec.recommendee_city, rec.recommendee_state].filter(Boolean).join(", ")}</span>}
              {rec.recommendee_website && (
                <a href={rec.recommendee_website} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline inline-flex items-center gap-0.5">
                  <ExternalLink className="w-3 h-3" /> site
                </a>
              )}
            </p>
          </div>
        </div>

        <div className="text-right text-xs text-[#94A3B8] shrink-0">
          <p>Recommended by</p>
          {rec.recommender ? (
            <a href={`/business/${rec.recommender.slug}`} target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-[#A78BFA] inline-flex items-center gap-1">
              {rec.recommender.name} <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <p className="text-white font-semibold">{rec.recommender_slug}</p>
          )}
          <p className="text-[10px] text-[#64748B] mt-0.5">{new Date(rec.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      {rec.endorsement_text && (
        <blockquote className="text-xs text-[#E2E8F0] italic border-l-2 border-[#7C3AED]/50 pl-2.5 mb-3">
          "{rec.endorsement_text}"
        </blockquote>
      )}
      {rec.personal_note && (
        <div className="mb-3 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2.5 py-2">
          <strong className="text-white">Private note from recommender:</strong> {rec.personal_note}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        {/* Status */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Status</label>
          <select
            value={rec.status}
            onChange={(e) => onPatch({ status: e.target.value })}
            disabled={saving}
            className="w-full bg-[#020817] border border-white/10 text-white rounded-md px-2.5 py-1.5 text-sm"
            style={{ colorScheme: "dark" }}
            data-testid={`admin-rec-status-${rec.id}`}
          >
            {STATUS_OPTIONS.filter((o) => o.v).map((o) => (
              <option key={o.v} value={o.v} className="bg-[#0F172A]">{o.label}</option>
            ))}
          </select>
        </div>

        {/* Linked slug */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Link to Listing (slug)</label>
          <div className="flex gap-1">
            <input
              value={linkedSlug}
              onChange={(e) => setLinkedSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
              placeholder="acme-hvac"
              className="flex-1 bg-[#020817] border border-white/10 text-white rounded-md px-2.5 py-1.5 text-sm font-mono"
              data-testid={`admin-rec-slug-${rec.id}`}
            />
            <button
              onClick={() => onPatch({ linked_slug: linkedSlug || null })}
              disabled={saving || linkedSlug === (rec.linked_slug || "")}
              className="px-2.5 rounded-md bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-xs disabled:opacity-40"
              data-testid={`admin-rec-link-${rec.id}`}
            >Save</button>
          </div>
        </div>

        {/* Trusted Network Visibility toggle */}
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Trusted Network</label>
          <button
            onClick={() => onPatch({ trusted_network_visible: !rec.trusted_network_visible })}
            disabled={saving || !rec.linked_slug}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
              rec.trusted_network_visible
                ? "bg-green-500/20 border-green-500/40 text-green-300"
                : "bg-white/5 border-white/10 text-[#94A3B8] hover:bg-white/10"
            } disabled:opacity-40`}
            data-testid={`admin-rec-visible-${rec.id}`}
            title={!rec.linked_slug ? "Link to a listing first" : ""}
          >
            {rec.trusted_network_visible ? <><Eye className="w-3.5 h-3.5" /> Visible</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}
          </button>
        </div>
      </div>

      {/* Fee */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        {rec.referral_fee_amount != null ? (
          <span className="inline-flex items-center gap-1 text-[#FBBF24]">
            <DollarSign className="w-3.5 h-3.5" /> ${rec.referral_fee_amount}
          </span>
        ) : (
          <span className="text-[#64748B]">No fee</span>
        )}
        <select
          value={rec.referral_fee_status || "n_a"}
          onChange={(e) => onPatch({ referral_fee_status: e.target.value })}
          disabled={saving}
          className="bg-[#020817] border border-white/10 text-white rounded-md px-2 py-1 text-[11px]"
          style={{ colorScheme: "dark" }}
          data-testid={`admin-rec-fee-status-${rec.id}`}
        >
          {["pending", "earned", "paid", "n_a"].map((s) => (
            <option key={s} value={s} className="bg-[#0F172A]">{s === "n_a" ? "n/a" : s}</option>
          ))}
        </select>

        <button onClick={() => setShowNotes((v) => !v)} className="ml-auto text-[#A78BFA] hover:text-white" data-testid={`admin-rec-notes-toggle-${rec.id}`}>
          {showNotes ? "Hide notes" : "Add admin note"}
        </button>
      </div>

      {showNotes && (
        <div className="mt-3 flex gap-2">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes about this lead…"
            className="flex-1 bg-[#020817] border border-white/10 text-white rounded-md px-2.5 py-1.5 text-xs"
            data-testid={`admin-rec-notes-${rec.id}`}
          />
          <button
            onClick={() => onPatch({ admin_notes: notes })}
            disabled={saving}
            className="px-3 rounded-md bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-xs"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
        </div>
      )}
    </motion.div>
  );
}
